import { Head, useForm, usePage } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Props {
  rates: {
    mileage_bill_rate: number
    mileage_pay_rate: number
    travel_pay_rate: number
    perdiem_breakfast: number
    perdiem_lunch: number
    perdiem_dinner: number
  }
  errors?: Record<string, string[]>
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Manage", href: "/admin/default_rates" },
  { title: "Default Rates", href: "/admin/default_rates" },
]

function formatRate(value: number): string {
  if (value === 0) return ""
  return value.toString()
}

export default function Show({ rates, errors }: Props) {
  const { flash } = usePage<{ flash: { notice?: string; alert?: string } }>()
    .props

  const { data, setData, patch, processing } = useForm({
    mileage_bill_rate: formatRate(rates.mileage_bill_rate),
    mileage_pay_rate: formatRate(rates.mileage_pay_rate),
    travel_pay_rate: formatRate(rates.travel_pay_rate),
    perdiem_breakfast: formatRate(rates.perdiem_breakfast),
    perdiem_lunch: formatRate(rates.perdiem_lunch),
    perdiem_dinner: formatRate(rates.perdiem_dinner),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch("/admin/default_rates")
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Default Rates" />

      <div className="p-6">
        <h1 className="border-hpi-light-gray mb-6 border-b pb-2 text-2xl font-semibold">
          Default Rates
        </h1>

        {flash?.notice && (
          <div className="mb-4 rounded bg-green-100 px-4 py-2 text-green-800">
            {flash.notice}
          </div>
        )}
        {flash?.alert && (
          <div className="mb-4 rounded bg-red-100 px-4 py-2 text-red-800">
            {flash.alert}
          </div>
        )}

        <form onSubmit={handleSubmit} className="max-w-xs">
          <section className="mb-6">
            <h3 className="mb-2 text-lg font-semibold">
              Mileage{" "}
              <span className="text-hpi-med-gray text-sm font-normal">
                $/mile
              </span>
            </h3>
            <div className="space-y-3">
              <div>
                <label
                  htmlFor="mileage_bill_rate"
                  className="mb-1 block font-medium"
                >
                  Bill Rate
                </label>
                <input
                  type="text"
                  id="mileage_bill_rate"
                  value={data.mileage_bill_rate}
                  onChange={(e) => setData("mileage_bill_rate", e.target.value)}
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
                {errors?.mileage_bill_rate && (
                  <p className="text-hpi-red mt-1 text-sm">
                    {errors.mileage_bill_rate.join(", ")}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="mileage_pay_rate"
                  className="mb-1 block font-medium"
                >
                  Pay Rate
                </label>
                <input
                  type="text"
                  id="mileage_pay_rate"
                  value={data.mileage_pay_rate}
                  onChange={(e) => setData("mileage_pay_rate", e.target.value)}
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
                {errors?.mileage_pay_rate && (
                  <p className="text-hpi-red mt-1 text-sm">
                    {errors.mileage_pay_rate.join(", ")}
                  </p>
                )}
              </div>
            </div>
          </section>

          <section className="mb-6">
            <h3 className="mb-2 text-lg font-semibold">
              Travel{" "}
              <span className="text-hpi-med-gray text-sm font-normal">
                $/hour
              </span>
            </h3>
            <div>
              <label
                htmlFor="travel_pay_rate"
                className="mb-1 block font-medium"
              >
                Pay Rate
              </label>
              <input
                type="text"
                id="travel_pay_rate"
                value={data.travel_pay_rate}
                onChange={(e) => setData("travel_pay_rate", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
              {errors?.travel_pay_rate && (
                <p className="text-hpi-red mt-1 text-sm">
                  {errors.travel_pay_rate.join(", ")}
                </p>
              )}
            </div>
          </section>

          <section className="mb-6">
            <h3 className="mb-2 text-lg font-semibold">
              Per Diem{" "}
              <span className="text-hpi-med-gray text-sm font-normal">
                $/day
              </span>
            </h3>
            <div className="space-y-3">
              <div>
                <label
                  htmlFor="perdiem_breakfast"
                  className="mb-1 block font-medium"
                >
                  Breakfast
                </label>
                <input
                  type="text"
                  id="perdiem_breakfast"
                  value={data.perdiem_breakfast}
                  onChange={(e) => setData("perdiem_breakfast", e.target.value)}
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
                {errors?.perdiem_breakfast && (
                  <p className="text-hpi-red mt-1 text-sm">
                    {errors.perdiem_breakfast.join(", ")}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="perdiem_lunch"
                  className="mb-1 block font-medium"
                >
                  Lunch
                </label>
                <input
                  type="text"
                  id="perdiem_lunch"
                  value={data.perdiem_lunch}
                  onChange={(e) => setData("perdiem_lunch", e.target.value)}
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
                {errors?.perdiem_lunch && (
                  <p className="text-hpi-red mt-1 text-sm">
                    {errors.perdiem_lunch.join(", ")}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="perdiem_dinner"
                  className="mb-1 block font-medium"
                >
                  Dinner
                </label>
                <input
                  type="text"
                  id="perdiem_dinner"
                  value={data.perdiem_dinner}
                  onChange={(e) => setData("perdiem_dinner", e.target.value)}
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  maxLength={50}
                />
                {errors?.perdiem_dinner && (
                  <p className="text-hpi-red mt-1 text-sm">
                    {errors.perdiem_dinner.join(", ")}
                  </p>
                )}
              </div>
            </div>
          </section>

          <div>
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
